import React, { useCallback, useMemo } from 'react';

import {
  AccountManager,
  getActiveForms,
  NotificationManager,
  useVerificationCheck,
} from '@atlas/core';
import { handleFormSubmit, useActiveForm } from '@atlas/react-forms';
import { Loader } from '@atlas/ui';

import FormMobile from 'common/Form/FormMobile';
import accountShape from 'common/PropTypes/accountShape';
import { snakeCase } from 'common/utils/snakeCase';
import { useFaceCheck } from 'components/ClientProfile/useFaceCheck';

const PROFILE_UPDATE_SIMPLE = 'PROFILE_UPDATE_SIMPLE';

const ProfileUpdateForm = ({ account }) => {
  const { docNumber, firstName, lastName } = account;

  let resetActiveFormHandler; // function value will be fetched later from useActiveForm
  const action = useCallback(
    handleFormSubmit(async (...params) => {
      await AccountManager.profileUpdateWithForm.bind(AccountManager)(...params);
      await resetActiveFormHandler();
    }),
    [],
  );

  const { isNeedFaceCheck } = useFaceCheck();

  const { verify } = useVerificationCheck({
    docNumber,
    firstName,
    lastName,
    onError: () => {
      NotificationManager.notify({
        message: 'legitimuz_face_check_error',
        status: 'error',
        type: 'notification',
      });
    },
    onSuccess: () => {
      submitHandler();
    },
    verificationType: 'facecheck',
  });

  const submitHandler = useCallback(() => {
    if (isNeedFaceCheck) return;

    NotificationManager.notify({
      message: 'update_profile_success_title',
      status: 'success',
      type: 'notification',
    });
  }, [isNeedFaceCheck]);

  const formData = useActiveForm('PROFILE_UPDATE', action, getActiveForms);
  const { types } = formData;
  resetActiveFormHandler = formData.resetActiveForm;

  const initialValues = useMemo(
    () =>
      Object.keys(account).reduce((acc, key) => {
        const snakeKey = snakeCase(key).toUpperCase();
        acc[snakeKey] = account[key];
        return acc;
      }, {}),
    [account],
  );
  const profileUpdateSimpleType = useMemo(
    () => types.find(({ templateName }) => templateName === PROFILE_UPDATE_SIMPLE),
    [types],
  );

  if (!profileUpdateSimpleType) {
    return <Loader className="profile-loader relative" />;
  }

  const {
    id,
    steps: [form],
  } = profileUpdateSimpleType;

  return (
    <div className="profile-update-container">
      <div className="form-container">
        <FormMobile
          action={isNeedFaceCheck ? verify : action}
          additionalValues={{ id }}
          buttonText={isNeedFaceCheck ? 'verify' : 'Save'}
          disablePristine
          form={form}
          id={id}
          initialValues={initialValues}
          onSubmit={submitHandler}
        />
      </div>
    </div>
  );
};

ProfileUpdateForm.propTypes = { account: accountShape.isRequired };

export default ProfileUpdateForm;
